/**
 * @file DevicesList.tsx
 * @description Список устройств склада (вынесен из `WmsSettingsPage` в
 * phase19.23 этап A; phase19.31 зона «Склад» — общий `ResponsiveList`).
 *
 * Строка: имя устройства, вид и подключение; «по умолчанию» — метка без
 * точки, «Неактивно» — серый статус. Действия — меню «⋯»: «Сделать по
 * умолчанию» и «Удалить» последним (раньше — кнопка-значок без подписи и
 * сокращение «По умолч.»). Только отрисовка: запросы и подтверждения живут
 * в `WarehouseDevicesPanel`.
 */

import { Printer, Star, Trash2 } from "lucide-react";

import { Badge } from "../../ui/badge";
import type { ListColumn } from "../../ui/responsive-list-types";
import { ResponsiveList } from "../../ui/ResponsiveList";
import type { RowAction } from "../../ui/RowActionsMenu";
import { StatusPill } from "../../ui/StatusPill";
import { TwoLineCell } from "../../ui/TwoLineCell";
import { DEVICE_KIND_LABEL, DEVICE_TRANSPORT_LABEL, type DeviceConfigDto } from "../../../lib/api-wms";

interface Props {
  items: DeviceConfigDto[];
  onSetDefault: (id: string) => void;
  onRemove: (id: string) => void;
}

/** «Принтер этикеток · Сеть · Zebra». */
function kindLine(d: DeviceConfigDto): string {
  return [DEVICE_KIND_LABEL[d.kind], DEVICE_TRANSPORT_LABEL[d.transport], d.vendor].filter(Boolean).join(" · ");
}

/** Признаки устройства: «по умолчанию» и «неактивно». */
function DeviceMarks({ d }: { d: DeviceConfigDto }) {
  if (!d.isDefault && d.isActive) return null;
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {d.isDefault ? <Badge variant="outline">по умолчанию</Badge> : null}
      {!d.isActive ? <StatusPill tone="neutral">Неактивно</StatusPill> : null}
    </span>
  );
}

const COLUMNS: ListColumn<DeviceConfigDto>[] = [
  {
    key: "name",
    header: "Устройство",
    cell: (d) => <TwoLineCell primary={d.name} secondary={kindLine(d)} />,
    compact: "title",
    compactCell: (d) => d.name,
  },
  { key: "kind", header: "Вид", compactOnly: true, cell: kindLine, compact: "subtitle" },
  { key: "marks", header: "Признаки", width: "w-[200px]", cell: (d) => <DeviceMarks d={d} />, compact: "trailing" },
];

export function DevicesList({ items, onSetDefault, onRemove }: Props) {
  /** Действия строки — реестром; опасное — последним. */
  function actionsFor(d: DeviceConfigDto): RowAction[] {
    const actions: RowAction[] = [];
    if (!d.isDefault) actions.push({ key: "default", label: "Сделать по умолчанию", icon: Star, run: () => onSetDefault(d.id) });
    actions.push({ key: "remove", label: "Удалить устройство", icon: Trash2, danger: true, run: () => onRemove(d.id) });
    return actions;
  }

  return (
    <ResponsiveList
      aria-label="Устройства склада"
      rows={items}
      columns={COLUMNS}
      rowKey={(d) => d.id}
      rowActions={actionsFor}
      rowActionsTitle={(d) => `Действия с устройством ${d.name}`}
      rowSize="double"
      empty={{ icon: Printer, title: "Устройств пока нет", description: "Добавьте принтер этикеток, сканер, весы или кассу." }}
    />
  );
}
